import {
  APIComponentAttributes,
  APIEntityType,
  BooleanOperator,
  HasReferenceFilterRule,
  Operator,
  QueryBuilderRow,
  QueryBuilderSubquery,
  ReferenceFilterIds,
} from '@ardoq/api-types';
import { formatDateISO, parseDate } from '@ardoq/date-time';
import {
  CREATED_BY,
  LAST_MODIFIED_BY,
  LAST_UPDATED,
  ReferenceTypeFilterIds,
} from '@ardoq/query-builder';
import { ColDef } from 'ag-grid-enterprise';
import { columnOperations } from '../columns/columnOperations';
import {
  AGGridPredefinedFilterOperator,
  ArtificialColumnDefinitionArdoqMetadata,
  ColumnDefinitionArdoqMetadata,
  ReferenceColumnDefinitionArdoqMetadata,
} from '../types';
import {
  hasValueSelectOption,
  noValueSelectOption,
  predefinedSelectOptions,
} from './agGridFilterOperations';
import {
  DateFilterEntry,
  FilterEntry,
  FilterModel,
  NestedFilterEntry,
  SetFilterEntry,
} from './types';

type FilterableColumnDefinitionArdoqMetadata = Exclude<
  ColumnDefinitionArdoqMetadata,
  ArtificialColumnDefinitionArdoqMetadata
>;

export const mapFilterModelToSubquery = <TData = APIComponentAttributes>(
  filterModel: FilterModel,
  columnDefinitions: ColDef<TData>[]
): QueryBuilderSubquery => {
  const rules = Object.entries(filterModel).map(([colId, filterEntry]) => {
    const columnDefinition = columnOperations.findColumnDefByColumnId(
      columnDefinitions,
      colId
    )!;
    return mapFilterEntryToSubqueryRule(
      columnOperations.getBackendFieldKey(columnDefinition)!,
      filterEntry,
      columnDefinition
    );
  });
  return { condition: BooleanOperator.AND, rules };
};

const mapAGDateFilterToArdoqFormat = (
  filterEntry: DateFilterEntry,
  columnId: string,
  columnMetadata: FilterableColumnDefinitionArdoqMetadata
): QueryBuilderRow => {
  if (
    columnMetadata.inventoryColumnType === 'field' &&
    columnMetadata.isDateRangeColumn
  ) {
    return {
      condition: BooleanOperator.OR,
      rules: [
        mapAGDateFilterToArdoqFormat(filterEntry, `${columnId}_start_date`, {
          ...columnMetadata,
          isDateRangeColumn: false,
        }),
        mapAGDateFilterToArdoqFormat(filterEntry, `${columnId}_end_date`, {
          ...columnMetadata,
          isDateRangeColumn: false,
        }),
      ],
    };
  }

  if (
    filterEntry.dateFrom &&
    filterEntry.dateTo &&
    filterEntry.type === 'inRange'
  ) {
    return {
      id: mapColumnIdToArdoqFieldId(
        columnId,
        filterEntry.dateFrom,
        columnMetadata
      ),
      field: mapColumnIdToArdoqFieldId(
        columnId,
        filterEntry.dateFrom,
        columnMetadata
      ),
      operator: Operator.BETWEEN,
      type: columnMetadata.queryBuilderType,
      input: columnMetadata.ardoqInputType,
      value: [
        mapAgGridValueToArdoqValue(
          filterEntry.dateFrom,
          filterEntry.filterType
        ) as string,
        mapAgGridValueToArdoqValue(
          filterEntry.dateTo,
          filterEntry.filterType
        ) as string,
      ],
      targetEntityType: APIEntityType.COMPONENT,
    };
  }

  return {
    id: mapColumnIdToArdoqFieldId(
      columnId,
      filterEntry.dateFrom,
      columnMetadata
    ),
    field: mapColumnIdToArdoqFieldId(
      columnId,
      filterEntry.dateFrom,
      columnMetadata
    ),
    operator: mapAGGridFilterToOperator(filterEntry.type),
    type: columnMetadata.queryBuilderType,
    input: columnMetadata.ardoqInputType,
    value: mapAgGridValueToArdoqValue(
      filterEntry.dateFrom,
      filterEntry.filterType
    ),
    targetEntityType: APIEntityType.COMPONENT,
  };
};

const mapOperatorFromAGSetFilterToArdoqFormat = (
  value: string | number | null
): Operator.IS_NOT_EMPTY | Operator.IS_EMPTY | Operator.EQUAL => {
  if (value === noValueSelectOption) {
    return Operator.IS_EMPTY;
  }
  if (value === hasValueSelectOption) {
    return Operator.IS_NOT_EMPTY;
  }
  return Operator.EQUAL;
};

const mapSetAGFilterToArdoqFormat = (
  filterEntry: SetFilterEntry,
  columnId: string,
  columnMetadata: FilterableColumnDefinitionArdoqMetadata
): QueryBuilderSubquery => {
  return {
    condition: BooleanOperator.OR,
    rules: filterEntry.values.map(value => ({
      id: mapColumnIdToArdoqFieldId(columnId, value, columnMetadata),
      field: mapColumnIdToArdoqFieldId(columnId, value, columnMetadata),
      operator: mapOperatorFromAGSetFilterToArdoqFormat(value),
      type: columnMetadata.queryBuilderType,
      input: columnMetadata.ardoqInputType,
      value: isPredefinedSelectOption(value)
        ? mapPredefinedSelectOptionToArdoqValue(value)
        : mapAgGridValueToArdoqValue(value, columnMetadata.queryBuilderType),
      targetEntityType: APIEntityType.COMPONENT,
    })),
  };
};

const mapReferenceAGFilterToArdoqFormat = (
  filterEntry: SetFilterEntry,
  columnMetadata: ReferenceColumnDefinitionArdoqMetadata
): QueryBuilderSubquery => {
  const directionOperator =
    columnMetadata.direction === 'outgoing' ? Operator.TO : Operator.FROM;

  return {
    condition: BooleanOperator.OR,
    rules: filterEntry.values.map(value => {
      if (value === predefinedSelectOptions.noReferences) {
        const filterId =
          columnMetadata.direction === 'outgoing'
            ? ReferenceTypeFilterIds.DOES_NOT_HAVE_OUTGOING_REFERENCE_TYPE
            : ReferenceTypeFilterIds.DOES_NOT_HAVE_INCOMING_REFERENCE_TYPE;
        return {
          id: filterId,
          field: filterId,
          operator: Operator.EQUAL,
          type: 'string',
          input: 'text',
          value: columnMetadata.referenceTypeName,
          targetEntityType: APIEntityType.COMPONENT,
        };
      }

      if (value === predefinedSelectOptions.hasReferences) {
        const filterId =
          columnMetadata.direction === 'outgoing'
            ? ReferenceTypeFilterIds.HAS_OUTGOING_REFERENCE_TYPE
            : ReferenceTypeFilterIds.HAS_INCOMING_REFERENCE_TYPE;
        return {
          id: filterId,
          field: filterId,
          operator: Operator.EQUAL,
          type: 'string',
          input: 'text',
          value: columnMetadata.referenceTypeName,
          targetEntityType: APIEntityType.COMPONENT,
        };
      }

      return {
        id: ReferenceFilterIds.HAS_REFERENCE,
        field: ReferenceFilterIds.HAS_REFERENCE,
        operator: directionOperator,
        type: columnMetadata.queryBuilderType,
        input: columnMetadata.ardoqInputType,
        value: value,
        targetEntityType: APIEntityType.COMPONENT,
        referenceTypeName: columnMetadata.referenceTypeName,
      } satisfies HasReferenceFilterRule;
    }),
  };
};

const mapPredefinedSelectOptionToArdoqValue = (
  value: string
): string | null => {
  if (value === noValueSelectOption || value === hasValueSelectOption) {
    return null;
  }
  return value;
};

const isPredefinedSelectOption = (
  value: string | number | null
): value is string => {
  return value === noValueSelectOption || value === hasValueSelectOption;
};

const mapNestedAGFilterToArdoqSubquery = <TData>(
  filterEntry: NestedFilterEntry,
  columnId: string,
  columnDefinition: ColDef<TData>
): QueryBuilderSubquery => {
  return {
    condition:
      filterEntry.operator === 'AND' ? BooleanOperator.AND : BooleanOperator.OR,
    rules: filterEntry.conditions.map((condition: FilterEntry) =>
      mapFilterEntryToSubqueryRule(columnId, condition, columnDefinition)
    ),
  };
};

const mapFilterEntryToSubqueryRule = <TData>(
  columnId: string,
  filterEntry: FilterEntry,
  columnDefinition: ColDef<TData>
): QueryBuilderRow => {
  const columnMetadata =
    columnDefinition.context as FilterableColumnDefinitionArdoqMetadata;

  if (
    columnMetadata.inventoryColumnType === 'reference' &&
    filterEntry.filterType === 'set'
  ) {
    return mapReferenceAGFilterToArdoqFormat(filterEntry, columnMetadata);
  }
  if (filterEntry.filterType === 'set') {
    return mapSetAGFilterToArdoqFormat(filterEntry, columnId, columnMetadata);
  }
  if ('operator' in filterEntry) {
    return mapNestedAGFilterToArdoqSubquery(
      filterEntry,
      columnId,
      columnDefinition
    );
  }
  if (filterEntry.filterType === 'date') {
    return mapAGDateFilterToArdoqFormat(filterEntry, columnId, columnMetadata);
  }

  return {
    id: mapColumnIdToArdoqFieldId(columnId, filterEntry.filter, columnMetadata),
    field: mapColumnIdToArdoqFieldId(
      columnId,
      filterEntry.filter,
      columnMetadata
    ),
    operator: mapAGGridFilterToOperator(filterEntry.type),
    type: columnMetadata.queryBuilderType,
    input: columnMetadata.ardoqInputType,
    value:
      filterEntry.filter !== null && filterEntry.filter !== undefined
        ? mapAgGridValueToArdoqValue(
            filterEntry.filter,
            columnMetadata.queryBuilderType
          )
        : null,
    targetEntityType: APIEntityType.COMPONENT,
  };
};

const mapReferenceTypeColumnIdToArdoqFieldId = (
  value: string | number | null | boolean,
  columnId: string
) => {
  if (value === predefinedSelectOptions.noReferences) {
    return columnOperations.columnIdIsForOutgoingReference(columnId)
      ? ReferenceTypeFilterIds.DOES_NOT_HAVE_OUTGOING_REFERENCE_TYPE
      : ReferenceTypeFilterIds.DOES_NOT_HAVE_INCOMING_REFERENCE_TYPE;
  }
  return columnOperations.columnIdIsForOutgoingReference(columnId)
    ? ReferenceTypeFilterIds.HAS_OUTGOING_REFERENCE_TYPE
    : ReferenceTypeFilterIds.HAS_INCOMING_REFERENCE_TYPE;
};

const mapColumnIdToArdoqFieldId = (
  columnId: string,
  value: string | null | number | boolean,
  columnMetadata: FilterableColumnDefinitionArdoqMetadata
) => {
  if (columnMetadata.inventoryColumnType === 'reference') {
    return mapReferenceTypeColumnIdToArdoqFieldId(value, columnId);
  }
  if (columnId === 'type') {
    return 'typeName';
  }

  /*
   * There's an inconsistency in which casing is used for field names in columns vs query builder. columns use the camel case version,
   * the query builder is still stuck on using the lispy case version. We are working on migrating the last lispy fields to camelCase.
   * In the meantime we have to map those names here. See https://ardoqcom.atlassian.net/browse/ARD-26178 for progress on the topic.
   */
  if (columnId === 'lastModifiedBy') {
    return LAST_MODIFIED_BY.id;
  }
  if (columnId === 'lastUpdated') {
    return LAST_UPDATED.id;
  }
  if (columnId === 'createdBy') {
    return CREATED_BY.id;
  }
  return columnId;
};

const mapAgGridValueToArdoqValue = (
  value: string | null | number | boolean,
  type: string
) => {
  if (value === null) {
    return null;
  }
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'boolean') {
    return value;
  }

  if (type === 'number') {
    return parseFloat(value);
  }
  if (type === 'boolean') {
    return value === 'true';
  }
  if (type === 'date') {
    return formatDateISO(parseDate(value));
  }
  return value;
};

const mapAGGridFilterToOperator = (
  filterOperator: AGGridPredefinedFilterOperator
): Operator => {
  switch (filterOperator) {
    case 'equals':
      return Operator.EQUAL;
    case 'notEqual':
      return Operator.NOT_EQUAL;
    case 'contains':
      return Operator.CONTAINS_SUBSTRING;
    case 'notContains':
      return Operator.NOT_CONTAINS_SUBSTRING;
    case 'startsWith':
    case 'endsWith':
      return Operator.CONTAINS_SUBSTRING; // we excluded these operators from the UI, so we can safely map them to contains
    case 'lessThan':
      return Operator.LESS;
    case 'lessThanOrEqual':
      return Operator.LESS_OR_EQUAL;
    case 'greaterThan':
      return Operator.GREATER;
    case 'greaterThanOrEqual':
      return Operator.GREATER_OR_EQUAL;
    case 'inRange': // TODO AM handle this case later
      return Operator.BETWEEN;
    case 'blank':
      return Operator.IS_EMPTY;
    case 'notBlank':
      return Operator.IS_NOT_EMPTY;
    default:
      filterOperator satisfies never;
      return Operator.EQUAL;
  }
};
